/**
 * Dynamic FAQ content for auction detail pages.
 *
 * Produces a de-duplicated, auction-specific list of question/answer pairs
 * that is rendered as schema.org FAQPage JSON-LD in the route `head()`.
 * Only questions relevant to the current listing are emitted (e.g. the
 * grading question is skipped for accessories, Buy It Now only appears when
 * the seller enabled it).
 */

export type FaqAuction = {
  title?: string | null;
  product_type?: string | null;
  grading_company?: string | null;
  custom_grading_company?: string | null;
  grade?: string | null;
  custom_grade?: string | null;
  grading_status?: string | null;
  grading_cert_number?: string | null;
  certification_number?: string | null;
  auto_extend_enabled?: boolean | null;
  auto_extend_seconds?: number | null;
  auto_extend_trigger_seconds?: number | null;
  buy_now_enabled?: boolean | null;
  buy_now_price_pence?: number | null;
};

export type FaqItem = { q: string; a: string };

function txt(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  if (!s || /^(raw|none|n\/a|other)$/i.test(s)) return null;
  return s;
}

function gbp(pence: number): string {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(pence / 100);
}

export function buildAuctionFaq(a: FaqAuction): FaqItem[] {
  const items: FaqItem[] = [];
  const name = txt(a.title) ?? "this item";
  const product = (a.product_type ?? "trading_card").toLowerCase();
  const isAccessory = product === "accessory";
  const isSealed = product === "sealed_product";
  const grader = txt(a.grading_company) ?? txt(a.custom_grading_company);
  const grade = txt(a.grade) ?? txt(a.custom_grade);
  const cert = txt(a.grading_cert_number) ?? txt(a.certification_number);

  items.push({
    q: "How does bidding work on BidNova Auctions?",
    a: `Create a free BidNova account, then place a bid on ${name} at or above the next minimum increment shown on the listing. Bids are binding and placed in GBP. If someone outbids you, you are notified instantly and can bid again until the countdown reaches zero. When the timer ends, the highest bid wins.`,
  });

  if (a.auto_extend_enabled !== false) {
    const extend = a.auto_extend_seconds ?? 30;
    const trigger = a.auto_extend_trigger_seconds ?? 30;
    items.push({
      q: "What is Auto-Extend?",
      a: `Auto-Extend is BidNova's anti-sniping protection. If a bid is placed in the final ${trigger} seconds of this auction, the countdown automatically extends by a further ${extend} seconds so every bidder gets a fair chance to respond. Extensions repeat for as long as bidding continues.`,
    });
  }

  if (a.buy_now_enabled && a.buy_now_price_pence) {
    items.push({
      q: "Can I buy this item immediately instead of bidding?",
      a: `Yes. This listing has Buy It Now enabled at ${gbp(a.buy_now_price_pence)}. Choosing Buy It Now ends the auction immediately and reserves the item for you at checkout. Buy It Now may be withdrawn once bidding starts.`,
    });
  }

  items.push({
    q: "When do I pay if I win?",
    a: "Payment is due as soon as the auction closes. Winning bidders receive an email and in-app notification with a secure Stripe checkout link and are expected to complete payment promptly so the seller can dispatch the item.",
  });

  items.push({
    q: "How is the item shipped?",
    a: "The seller dispatches once payment clears, using a tracked UK service such as Royal Mail, Evri or InPost, or local collection where offered. Trading cards are packed protectively and tracking details are added to your order so you can follow delivery from your BidNova dashboard.",
  });

  if (!isAccessory) {
    if (grader && grade) {
      items.push({
        q: "Is this trading card authenticated or graded?",
        a: `Yes. ${name} is graded ${grader} ${grade}${cert ? ` under certificate number ${cert}` : ""}. BidNova verifies certificate numbers against the grader's population data where the grading company supports lookups, and the slab is shown in the listing photographs.`,
      });
    } else if (isSealed) {
      items.push({
        q: "Is this sealed product authenticated?",
        a: `${name} is sold as a sealed product and is not third-party graded. Listing photographs show the original factory seal, and BidNova's buyer protection covers items that arrive misdescribed, tampered with or damaged.`,
      });
    } else {
      items.push({
        q: "Is this trading card authenticated or graded?",
        a: `${name} is listed as a raw (ungraded) card, so its condition is described and photographed by the seller rather than certified by PSA, BGS, CGC or SGC. Review the photographs closely — BidNova's buyer protection covers items that arrive materially misdescribed.`,
      });
    }
  }

  items.push({
    q: "What happens if I do not pay?",
    a: "Bids are binding. If a winning bidder does not pay, the order is cancelled, the seller may relist or offer the item to the next highest bidder, and repeated non-payment can lead to bidding restrictions or account suspension under BidNova's buyer terms.",
  });

  items.push({
    q: "How do seller fees work?",
    a: "BidNova charges a flat 8% commission on successful sales only. There are no listing fees, no monthly fees and no subscription fees. The commission is deducted automatically and the remaining balance is paid out to the seller via Stripe.",
  });

  // Guard against duplicate questions if the builder is ever extended.
  const seen = new Set<string>();
  return items.filter((i) => {
    const key = i.q.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** schema.org FAQPage JSON-LD script entry for a route's head().scripts. */
export function auctionFaqLd(items: FaqItem[]) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: items.map((i) => ({
        "@type": "Question",
        name: i.q,
        acceptedAnswer: { "@type": "Answer", text: i.a },
      })),
    }),
  };
}
